import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import hcMark from '../assets/images/hc-mark.png';
import wordmark from '../assets/images/header.png';
import { ReactComponent as TabbarSwitch } from '../assets/tabbar-switch.svg';
import { ReactComponent as HouseIcon } from '../assets/icons/house-filled.svg';
import { ReactComponent as BagIcon } from '../assets/icons/bag.svg';
import { ReactComponent as GiftIcon } from '../assets/icons/gift-filled.svg';
import { ReactComponent as PlaneIcon } from '../assets/icons/plane.svg';
import { ReactComponent as GamesIcon } from '../assets/icons/icon-games.svg';
import { ReactComponent as PersonIcon } from '../assets/icons/person.svg';
import { ReactComponent as SettingsIcon } from '../assets/icons/settings.svg';
import { ReactComponent as InfoIcon } from '../assets/icons/info-outline.svg';
import '../styles/Home.css';

const ShellUserContext = createContext({
  user: null,
  setUser: () => {},
});

export function useShellUser() {
  return useContext(ShellUserContext);
}

const NAV = [
  { id: 'home', label: 'Home', to: '/' },
  { id: 'shop', label: 'Shop', to: '/shop' },
  { id: 'rewards', label: 'Rewards', to: '/rewards' },
  { id: 'travel', label: 'Travel', to: '/travel' },
  { id: 'games', label: 'Games', to: '/games' },
];

const NAV_ICONS = {
  home: HouseIcon,
  shop: BagIcon,
  rewards: GiftIcon,
  travel: PlaneIcon,
  games: GamesIcon,
};

function formatPoints(value) {
  return (Number(value) || 0).toLocaleString('en-US');
}

function pickAvatar(user) {
  const raw = String(
    user?.avatar_url || user?.avatarUrl || user?.profile_image || user?.profileImage || ''
  ).trim();
  if (!raw) return '';
  if (/^(https?:|data:|blob:)/.test(raw)) return raw;
  if (raw.startsWith('s3://')) return raw.replace('s3://', 'https://');
  return `https://app.gethomecrowd.com/${raw.replace(/^\/+/, '')}`;
}

function currentNav(pathname) {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path === '/shop' || path.startsWith('/shop/')) return 'shop';
  if (path === '/rewards') return 'rewards';
  if (path === '/travel') return 'travel';
  if (path === '/games') return 'games';
  if (path === '/') return 'home';
  return '';
}

function AppShell({ title, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(() => apiService.getCurrentUser());
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => window.matchMedia('(max-width: 760px)').matches
  );
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'HomeCrowd fan';
  const availablePoints = Number(user?.current_points) || 0;
  const current = currentNav(location.pathname);
  const showTravel = user?.active_school?.travel_active !== false;
  const navItems = NAV.filter((item) => item.id !== 'travel' || showTravel);

  useEffect(() => {
    let cancelled = false;
    apiService.fetchCurrentUser().then((nextUser) => {
      if (!cancelled && nextUser) setUser(nextUser);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  const logout = () => {
    apiService.logout();
    navigate('/login');
  };

  return (
    <ShellUserContext.Provider value={{ user, setUser }}>
      <div className="home-shell">
        <aside className={`home-side${collapsed ? ' is-collapsed' : ''}`}>
          <div className="home-brand">
            {collapsed ? (
              <img src={hcMark} alt="HomeCrowd" className="home-brand-hc" />
            ) : (
              <img src={wordmark} alt="HomeCrowd" className="home-brand-word" />
            )}
            <button
              type="button"
              className="home-side-toggle"
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={() => setCollapsed((open) => !open)}
            >
              <TabbarSwitch />
            </button>
          </div>
          <nav className="home-nav" aria-label="Main">
            {navItems.map((item) => {
              const Icon = NAV_ICONS[item.id] || HouseIcon;
              if (!item.to) {
                return (
                  <span key={item.id} className="home-nav-item">
                    <Icon aria-hidden="true" />
                    <span className="home-nav-label">{item.label}</span>
                  </span>
                );
              }
              const active = current === item.id;
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  className={`home-nav-item${active ? ' is-current' : ''}`}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon aria-hidden="true" />
                  <span className="home-nav-label">{item.label}</span>
                </Link>
              );
            })}
          </nav>
          {collapsed ? null : <img src={hcMark} alt="" className="home-side-mark" />}
        </aside>

        <div className="home-main">
          <header className="home-top">
            <h1 className="home-top-title">{title}</h1>
            <div className="home-top-actions">
              <div className={`home-user${menuOpen ? ' is-open' : ''}`}>
                <button
                  type="button"
                  className="home-user-chip"
                  aria-expanded={menuOpen}
                  onClick={(event) => {
                    event.stopPropagation();
                    setMenuOpen((open) => !open);
                  }}
                >
                  <span className="home-avatar" aria-hidden="true">
                    {pickAvatar(user) ? <img src={pickAvatar(user)} alt="" /> : <PersonIcon />}
                  </span>
                  <span className="home-user-copy">
                    <span className="home-user-name">{fullName}</span>
                  </span>
                  <svg className="home-chevron" viewBox="0 0 16 18" aria-hidden="true">
                    <path d="M3.5 6.4 8 2.6l4.5 3.8" />
                    <path d="M3.5 11.6 8 15.4l4.5-3.8" />
                  </svg>
                </button>
                {menuOpen ? (
                  <div className="home-user-menu" onClick={(event) => event.stopPropagation()}>
                    <div className="home-user-menu-list">
                      <Link to="/profile" onClick={() => setMenuOpen(false)}>
                        <PersonIcon aria-hidden="true" />
                        View profile
                      </Link>
                      <Link to="/settings" onClick={() => setMenuOpen(false)}>
                        <SettingsIcon aria-hidden="true" />
                        Settings
                      </Link>
                      <Link to="/support/" onClick={() => setMenuOpen(false)}>
                        <InfoIcon aria-hidden="true" />
                        Help & support
                      </Link>
                    </div>
                    <button type="button" className="home-user-menu-logout" onClick={logout}>
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M10 7V5.5A1.5 1.5 0 0 0 8.5 4h-3A1.5 1.5 0 0 0 4 5.5v13A1.5 1.5 0 0 0 5.5 20h3A1.5 1.5 0 0 0 10 18.5V17" />
                        <path d="M14 16.5 18.5 12 14 7.5" />
                        <path d="M18.2 12H9" />
                      </svg>
                      Log out
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="home-points-pill">{formatPoints(availablePoints)} pts</div>
            </div>
          </header>
          <div className="home-body">{children}</div>
        </div>
      </div>
    </ShellUserContext.Provider>
  );
}

export default AppShell;
